import { Divider } from "#components/Divider";
import { Footnote } from "#components/Footnote";
import { Link } from "#components/Link";
import { Tooltip } from "#components/Tooltip";
import { useColors } from "#components/useColors";
import { useMounted, useOnMount } from "#components/useMounted";
import {
  Box,
  Flex,
  Icon,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const GraphContext = createContext<{
  active: number;
  unlocked: Set<number>;
  finalUnlocked: boolean;
  seen: Set<number>;
  traverse: (edge: number) => void;
  traversed: Set<number>;
  push: (node: number) => void;
  undo: (() => void) | null;
  redo: (() => void) | null;
  reset: () => void;
}>({
  active: 0,
  unlocked: new Set(),
  finalUnlocked: true,
  seen: new Set(),
  traverse: () => {},
  traversed: new Set(),
  push: () => {},
  undo: null,
  redo: null,
  reset: () => {},
});

const nodes: [number, number][] = [
  [0, 0.5],
  [0.25, 0],
  [0.5, 0],
  [0.75, 0],
  [0.25, 0.5],
  [0.5, 0.5],
  [0.75, 0.5],
  [0.2, 1],
  [0.4, 1],
  [0.6, 1],
  [0.8, 1],
  [1, 0.5],
];

const FINAL = 11;

const edges: [number, number][] = [
  // 0
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 11],

  // 4
  [0, 4],
  [4, 5],
  [5, 6],
  [6, 11],

  // 8
  [0, 7],
  [7, 8],
  [8, 9],
  [9, 10],
  [10, 11],

  // 13
  [1, 5],
  [4, 3],
  [5, 10],
  [7, 5],
  [9, 6],
  [2, 6],
  [5, 9],
];

function useLocalStorage<T>(
  name: string,
  {
    defaultValue,
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    startWithDefault = false,
  }: {
    defaultValue: T;
    serialize?: (value: T) => string;
    deserialize?: (value: string) => T;
    startWithDefault?: boolean;
  },
): [T, (updater: (value: T) => T) => void] {
  const key = `dont-burn-out-graph-${name}`;
  const [value, setValue] = useState<T>(defaultValue);

  useOnMount(() => {
    if (!startWithDefault) {
      const unparsed = localStorage.getItem(key);
      if (unparsed) {
        setValue(deserialize(unparsed));
      }
    }
  });

  return [
    value,
    (updater) => {
      const newValue = updater(value);
      setValue(newValue);
      localStorage.setItem(key, serialize(newValue));
    },
  ];
}

export const GraphContainer = ({ children }: { children: ReactNode }) => {
  const [active, saveActive] = useState(0);
  const [historyStack, setHistoryStack] = useLocalStorage<{
    undo: number[];
    redo: number[];
  }>("historyStack", {
    defaultValue: {
      undo: [0],
      redo: [],
    },
  });
  const [traversed, setTraversed] = useLocalStorage<Set<number>>("traversed", {
    defaultValue: new Set(),
    serialize: (s) => JSON.stringify(Array.from(s)),
    deserialize: (s) => new Set(JSON.parse(s)),
  });

  const setActive = useCallback((i: number) => {
    saveActive(() => i);
    document
      .getElementsByTagName("header")?.[0]
      ?.scrollIntoView({ behavior: "smooth" });
    const url = new URL(window.location.href);
    url.hash = i === 0 ? "" : `#section-${i}`;
    window.history.replaceState({}, "", url.href);
  }, []);

  const push = useCallback(
    (i: number) => {
      setActive(i);
      setHistoryStack(({ undo }) => ({
        undo: [...undo, i].slice(-10),
        redo: [],
      }));
    },
    [setActive, setHistoryStack],
  );

  const undo = useCallback(() => {
    const i = historyStack.undo.at(-2);
    if (i === undefined) return;
    setActive(i);
    setHistoryStack(({ undo, redo }) => ({
      undo: undo.slice(0, -1),
      redo: [...redo, undo.at(-1)!],
    }));
  }, [historyStack.undo, setActive, setHistoryStack]);

  const redo = useCallback(() => {
    const i = historyStack.redo.at(-1);
    if (i === undefined) return;
    setActive(i);
    setHistoryStack(({ undo, redo }) => ({
      undo: [...undo, i],
      redo: redo.slice(0, -1),
    }));
  }, [historyStack.redo, setActive, setHistoryStack]);

  const unlocked = new Set([
    0,
    ...nodes
      .map((_, n) => n)
      .filter((n) =>
        edges.some(([, b], edge) => traversed.has(edge) && b === n),
      ),
  ]);

  const seen = new Set(
    edges.filter((e) => unlocked.has(e[0])).map((e) => e[1]),
  );

  useOnMount(() => {
    const target = parseInt(
      window.location.hash?.match(/#section-(\d+)/)?.[1] ?? "0",
    );
    if (nodes[target] === undefined) return;
    push(target);
  });

  return (
    <GraphContext.Provider
      value={{
        active,
        push: push,
        seen,
        finalUnlocked: unlocked.size >= nodes.length - 1,
        unlocked,
        traversed,
        traverse: (edge: number) => {
          const [a, b] = edges[edge];
          if (!unlocked.has(a)) {
            throw new Error("not unlocked");
          }
          push(b);
          setTraversed((s) => new Set([...s, edge]));
        },
        undo: historyStack.undo.length > 1 ? undo : null,
        redo: historyStack.redo.length > 0 ? redo : null,
        reset: () => {
          push(0);
          setHistoryStack(() => ({ undo: [0], redo: [] }));
          setTraversed(() => new Set());
        },
      }}
    >
      <Graph />
      <Controls />
      <Box position="relative">{children}</Box>
    </GraphContext.Provider>
  );
};

const Graph = () => {
  const { active, push, unlocked, finalUnlocked, seen, traversed } =
    useContext(GraphContext);
  const mounted = useMounted();
  const [dims, setDims] = useState({
    width: 0,
    height: 0,
    circleSize: 0,
    padding: 0,
    arrowLength: 0,
    arrowThickness: 0,
  });
  const svgRef = useRef<SVGSVGElement>(null);
  const activeColor = useColorModeValue("orange.400", "orange.400");
  const unlockedColor = useColorModeValue("gray.600", "whiteAlpha.800");
  const unlockedColorHover = useColorModeValue("orange.600", "orange.200");
  const unlockableColor = useColorModeValue("gray.400", "whiteAlpha.600");
  const lockedColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const edgeColor = useColorModeValue("#171923", "#ededee");

  useOnMount(() => {
    const observer = new ResizeObserver((elts) => {
      const { width, height } = elts[0].contentRect;
      const rem = parseFloat(
        window.getComputedStyle(document.documentElement).fontSize,
      );
      setDims({
        width,
        height,
        circleSize: rem,
        padding: 0.5 * rem,
        arrowLength: 0.4 * rem,
        arrowThickness: 0.25 * rem,
      });
    });
    if (svgRef.current) {
      observer.observe(svgRef.current);
    }
    return () => observer.disconnect();
  });

  const coord = (x: number, y: number) => {
    const cx = x * (dims.width - 2 * dims.padding) + dims.padding;
    const cy = y * (dims.height - 2 * dims.padding) + dims.padding;
    return [cx, cy];
  };

  return (
    <Box id="graph" width="full" aspectRatio={4} position="relative">
      <svg
        ref={svgRef}
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <defs>
          <marker
            id="arrow"
            markerWidth={dims.arrowLength}
            markerHeight={dims.arrowThickness}
            refX={0}
            refY={dims.arrowThickness / 2}
            orient="auto-start-reverse"
            markerUnits="strokeWidth"
          >
            <path
              d={`M0 0 L0 ${dims.arrowThickness} L${dims.arrowLength} ${dims.arrowThickness / 2} z`}
              fill={edgeColor}
            />
          </marker>
        </defs>
        {edges.map(([a, b], i) => {
          const start = coord(...nodes[a]);
          const end = coord(...nodes[b]);
          const angle = Math.atan2(end[1] - start[1], end[0] - start[0]);
          const newStart = [
            start[0] + Math.cos(angle) * (dims.circleSize / 2),
            start[1] + Math.sin(angle) * (dims.circleSize / 2),
          ];
          const newEnd = [
            end[0] -
              Math.cos(angle) * (dims.arrowLength * 2 + dims.circleSize / 2),
            end[1] -
              Math.sin(angle) * (dims.arrowLength * 2 + dims.circleSize / 2),
          ];
          return (
            <g key={i}>
              <path
                d={`M${newStart.join(" ")} L${newEnd.join(" ")}`}
                style={{
                  fill: "none",
                  stroke: edgeColor,
                  strokeWidth: 2,
                  strokeDasharray: traversed.has(i)
                    ? undefined
                    : `${dims.arrowLength} ${dims.arrowThickness}`,
                  opacity:
                    active === a && (b !== FINAL || finalUnlocked)
                      ? 1
                      : unlocked.has(a)
                        ? 0.2
                        : 0,
                  transition: "opacity 0.2s ease",
                }}
                markerEnd="url(#arrow)"
              />
            </g>
          );
        })}
      </svg>
      <Box
        position="absolute"
        style={{
          height: "calc(100% - 1rem)",
          width: "calc(100% - 1rem)",
          top: "0.5rem",
          left: "0.5rem",
        }}
      >
        {nodes.map(([x, y], i) => {
          if (!mounted) return;

          const realUnlocked =
            unlocked.has(i) && (i !== FINAL || finalUnlocked);
          const realUnlockable =
            edges.some(([a, b]) => a === active && b === i) &&
            (i !== FINAL || finalUnlocked);

          return (
            <Box
              key={i}
              position="absolute"
              height={4}
              width={4}
              borderRadius="full"
              top={`${y * 100}%`}
              left={`${x * 100}%`}
              transform="translate(-50%, -50%)"
              background={
                active === i
                  ? activeColor
                  : realUnlocked
                    ? unlockedColor
                    : realUnlockable
                      ? unlockableColor
                      : seen.has(i)
                        ? lockedColor
                        : "transparent"
              }
              transition="background 0.2s ease"
              {...(realUnlocked &&
                active !== i && {
                  cursor: "pointer",
                  onClick: () => push(i),
                  _hover: {
                    background: unlockedColorHover,
                  },
                })}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const Controls = () => {
  const { undo, redo, reset } = useContext(GraphContext);
  const [confirming, setConfirming] = useState(false);
  const mounted = useMounted();
  const { footnoteBackground, textColor } = useColors();
  const dangerBackground = useColorModeValue("red.100", "red.700");

  useEffect(() => {}, []);

  return (
    mounted && (
      <Flex my="parSpace" justify="center">
        <Tooltip label="Undo" bg={footnoteBackground} color={textColor}>
          <IconButton
            colorScheme="gray"
            variant="ghost"
            size="md"
            aria-label="Undo"
            icon={
              <Icon
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentcolor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 7v6h6" />
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
              </Icon>
            }
            isDisabled={!undo}
            onClick={() => undo?.()}
          />
        </Tooltip>
        <Tooltip label="Redo">
          <IconButton
            colorScheme="gray"
            variant="ghost"
            size="md"
            aria-label="Redo"
            icon={
              <Icon
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentcolor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 7v6h-6" />
                <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7" />
              </Icon>
            }
            isDisabled={!redo}
            onClick={() => redo?.()}
          />
        </Tooltip>
        <Tooltip
          label={confirming ? "Are you sure?" : "Reset all progress"}
          bg={confirming ? dangerBackground : footnoteBackground}
        >
          <IconButton
            colorScheme="gray"
            variant="ghost"
            size="md"
            aria-label={confirming ? "Are you sure?" : "Reset all progress"}
            icon={
              <Icon
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentcolor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </Icon>
            }
            onClick={(e) => {
              if (!confirming) {
                e.preventDefault();
                setConfirming(true);
              } else {
                reset();
                setConfirming(false);
              }
            }}
            onBlur={() => setConfirming(false)}
            onPointerLeave={() => setConfirming(false)}
            transition="background 0.2s ease"
            _hover={{ bg: confirming ? dangerBackground : footnoteBackground }}
            _focus={{ bg: confirming ? dangerBackground : footnoteBackground }}
          />
        </Tooltip>
      </Flex>
    )
  );
};

export const Section = ({
  children,
  id,
}: {
  children: ReactNode;
  id: number;
}) => {
  const { active } = useContext(GraphContext);
  const mounted = useMounted();
  const [visible, setVisible] = useState(false);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const shouldBeVisible = active === id;
    if (visible && !shouldBeVisible) {
      setVisible(false);
      setTimeout(() => setRendered(false), 300);
    } else if (!visible && shouldBeVisible) {
      setRendered(true);
      setTimeout(() => setVisible(true), 300);
    }
  }, [active, id, visible]);

  return (
    (!mounted || rendered) && (
      <Box
        id={`section-${id}`}
        opacity={!mounted || visible ? 1 : 0}
        transition="opacity 0.3s ease-out"
      >
        {!mounted && <Divider />}
        {children}
      </Box>
    )
  );
};

export const SectionLink = ({
  children,
  id,
}: {
  children: ReactNode;
  id: number;
}) => {
  const mounted = useMounted();
  const { unlocked, finalUnlocked, traverse } = useContext(GraphContext);
  const target = edges[id][1];
  const locked = target === FINAL && !finalUnlocked;
  const { subtitleColor } = useColors();

  return mounted && locked ? (
    <Footnote content="Read all other sections to unlock.">{children}</Footnote>
  ) : (
    <Link
      href={`#section-${target}`}
      onClick={(e) => {
        e.preventDefault();
        traverse(id);
      }}
      {...(unlocked.has(target) ? { color: subtitleColor } : {})}
    >
      {children}
    </Link>
  );
};
